// Transmission 3D logo (#70, #75): extrudes the inline SVG wordmark into a
// chrome solid with three.js, with the brand's three stars as faceted chrome
// stars that launch from their logo formation into an orbit and back.
// A pre-rendered chrome image stays as fallback until the first WebGL frame
// renders (no WebGL, CDN blocked, JS error).
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.186.0/+esm';
import { SVGLoader } from 'https://cdn.jsdelivr.net/npm/three@0.186.0/examples/jsm/loaders/SVGLoader.js/+esm';
import { RoomEnvironment } from 'https://cdn.jsdelivr.net/npm/three@0.186.0/examples/jsm/environments/RoomEnvironment.js/+esm';

const POINTER_SMOOTHING = 3; // higher = follows the pointer faster
const CANVAS_RATIO = 0.44; // canvas height / width (fallback image must match)
const LOGO_LIFT = 130; // raises the wordmark so the star formation fits below it

// One choreography cycle, in seconds: formation (with the flip) -> launch ->
// one orbit lap -> return to formation.
const CYCLE = 21;
const FLIP_AT = 1.2;
const FLIP_DURATION = 1.8;
const LAUNCH_AT = 4.5;
const LAUNCH_DURATION = 2.4;
const ORBIT_SPEED = 0.5; // radians per second
const RETURN_AT = LAUNCH_AT + (Math.PI * 2) / ORBIT_SPEED; // one full lap: back where they launched
const RETURN_DURATION = 2.6;

// Orbit ellipse, in SVG units (the wordmark is ~3600 wide, ~680 tall)
const ORBIT_RX = 1950;
const ORBIT_RZ = 700;
const ORBIT_TILT = 0.22;

// Star layout mirrors the brand mark (Estrellas 1-03): big star in front, two
// smaller stars slightly higher, tucked behind and leaning in. `orbitAngle` is
// where each star joins the ring; `delay` staggers the big star behind the sides.
const STAR_RADIUS = 160;
const FORMATION_Y = -560;
const STARS = [
  { scale: 1, formation: [0, FORMATION_Y, 24], tilt: 0, orbitAngle: -Math.PI / 2, delay: 0.35 },
  { scale: 0.77, formation: [-186, FORMATION_Y + 32, -64], tilt: -0.3, orbitAngle: Math.PI, delay: 0 },
  { scale: 0.77, formation: [186, FORMATION_Y + 32, -64], tilt: 0.3, orbitAngle: 0, delay: 0 },
];

const clamp01 = (x) => Math.min(Math.max(x, 0), 1);
const easeInOutCubic = (x) => (x < 0.5 ? 4 * x ** 3 : 1 - (-2 * x + 2) ** 3 / 2);
// Eases in and out with a small anticipation dip and a small overshoot, so
// the stars wind up before launching and settle when they land.
const easeInOutBack = (x) => {
  const c = 1.2 * 1.525;
  return x < 0.5
    ? ((2 * x) ** 2 * ((c + 1) * 2 * x - c)) / 2
    : ((2 * x - 2) ** 2 * ((c + 1) * (2 * x - 2) + c) + 2) / 2;
};

// Faceted 5-point star: every edge rises to a raised centre point on both
// sides (a "cut" Y2K star rather than a flat extrusion). Non-indexed
// geometry so each facet is flat-shaded and catches its own reflection.
function facetedStar(radius, height) {
  const rim = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? radius : radius * 0.45;
    const a = Math.PI / 2 + (i * Math.PI) / 5;
    rim.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  const positions = [];
  for (let i = 0; i < 10; i++) {
    const [x1, y1] = rim[i];
    const [x2, y2] = rim[(i + 1) % 10];
    positions.push(0, 0, height, x1, y1, 0, x2, y2, 0);
    positions.push(0, 0, -height, x2, y2, 0, x1, y1, 0);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

// Where one star is at cycle time `tau` (global time `t` drives idle wobble).
// `w` blends formation (0) -> orbit (1) with zero velocity at both ends; the
// launch and landing each add one barrel roll that also starts and ends at
// rest, so every hand-off is seamless.
function placeStar(star, cfg, tau, t) {
  const s = tau - cfg.delay;
  let w = 0;
  let roll = 0;
  if (s >= RETURN_AT) {
    const p = clamp01((s - RETURN_AT) / RETURN_DURATION);
    w = 1 - easeInOutBack(p);
    roll = -Math.PI * 2 * easeInOutCubic(p);
  } else if (s >= LAUNCH_AT + LAUNCH_DURATION) {
    w = 1;
  } else if (s >= LAUNCH_AT) {
    const p = (s - LAUNCH_AT) / LAUNCH_DURATION;
    w = easeInOutBack(p);
    roll = Math.PI * 2 * easeInOutCubic(p);
  }

  const [fx, fy, fz] = cfg.formation;
  const idleY = Math.sin(t * 1.1 + fx * 0.01) * 14;

  const a = cfg.orbitAngle + Math.max(s - LAUNCH_AT, 0) * ORBIT_SPEED;
  const breathe = 1 + Math.sin(t * 0.7 + cfg.orbitAngle) * 0.05;
  const ox = Math.cos(a) * ORBIT_RX * breathe;
  const oz = Math.sin(a) * ORBIT_RZ * breathe;
  const oy = -oz * ORBIT_TILT + Math.sin(t * 1.3 + cfg.orbitAngle) * 40;

  star.position.set(fx + (ox - fx) * w, fy + idleY + (oy - fy - idleY) * w, fz + (oz - fz) * w);
  star.rotation.set(
    (0.35 + Math.sin(t * 0.5 + cfg.orbitAngle) * 0.25) * w,
    Math.sin(t * 0.6 + cfg.orbitAngle) * 0.45 * w + Math.sin(t * 0.8 + fx) * 0.08 * (1 - w) + roll,
    cfg.tilt * (1 - w) + Math.sin(t * 0.4 + cfg.orbitAngle) * 0.6 * w
  );
}

const host = document.querySelector('.bxstr-transmission__logo');
const svg = host?.querySelector('svg');

if (host && svg) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.5;
  renderer.domElement.setAttribute('aria-hidden', 'true');

  const scene = new THREE.Scene();
  scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;

  const camera = new THREE.PerspectiveCamera(28, 1, 10, 100000);

  // Geometry: SVG path -> shapes -> extrusion, centred on the origin.
  const shapes = new SVGLoader().parse(svg.outerHTML).paths.flatMap((path) => path.toShapes(true));
  const geometry = new THREE.ExtrudeGeometry(shapes, {
    depth: 150,
    bevelEnabled: true,
    bevelThickness: 22,
    bevelSize: 9,
    bevelSegments: 4,
    curveSegments: 10,
  });
  geometry.center();

  const material = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    metalness: 1,
    roughness: 0.12,
    clearcoat: 1,
    clearcoatRoughness: 0.08,
    iridescence: 0.6,
    iridescenceIOR: 1.6,
    iridescenceThicknessRange: [200, 900],
  });

  // Wordmark and stars share one pivot, so in formation they move as one mark.
  const logo = new THREE.Mesh(geometry, material);
  logo.rotation.x = Math.PI; // SVG y axis points down
  const pivot = new THREE.Group();
  pivot.position.y = LOGO_LIFT;
  pivot.add(logo);
  scene.add(pivot);

  const stars = STARS.map((cfg) => {
    const star = new THREE.Mesh(facetedStar(STAR_RADIUS * cfg.scale, STAR_RADIUS * cfg.scale * 0.5), material);
    pivot.add(star);
    return star;
  });
  const placeStars = (tau, t) => stars.forEach((star, i) => placeStar(star, STARS[i], tau, t));
  placeStars(0, 0);

  // Coloured rim lights give the chrome something to reflect on a black page.
  const rimA = new THREE.PointLight(0x7aa2ff, 4, 0, 0);
  rimA.position.set(-2500, 800, 1500);
  const rimB = new THREE.PointLight(0xff7ad9, 3, 0, 0);
  rimB.position.set(2500, -600, 1200);
  scene.add(rimA, rimB);

  const width = geometry.boundingBox.max.x - geometry.boundingBox.min.x;

  const resize = () => {
    const w = host.clientWidth;
    const h = Math.round(w * CANVAS_RATIO);
    renderer.setSize(w, h, false);
    renderer.domElement.style.width = `${w}px`;
    renderer.domElement.style.height = `${h}px`;
    camera.aspect = w / h;
    // Distance that fits the full wordmark width with a little margin.
    camera.position.z = (width * 1.5) / 2 / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) / camera.aspect;
    camera.updateProjectionMatrix();
    if (reducedMotion) renderer.render(scene, camera);
  };

  // Pointer tilt eases toward the target instead of snapping to it, and
  // drifts back to centre when the pointer leaves the window.
  const target = { x: 0, y: 0 };
  const pointer = { x: 0, y: 0 };
  window.addEventListener('pointermove', (event) => {
    target.x = (event.clientX / window.innerWidth) * 2 - 1;
    target.y = (event.clientY / window.innerHeight) * 2 - 1;
  });
  document.documentElement.addEventListener('pointerleave', () => {
    target.x = 0;
    target.y = 0;
  });

  let last = performance.now() / 1000;
  const render = () => {
    const t = performance.now() / 1000;
    const ease = 1 - Math.exp(-Math.min(t - last, 0.1) * POINTER_SMOOTHING);
    last = t;
    pointer.x += (target.x - pointer.x) * ease;
    pointer.y += (target.y - pointer.y) * ease;
    if (!reducedMotion) {
      // Readable sway facing front, one eased full flip per cycle while the
      // stars are in formation, plus a gentle float and pointer tilt.
      const tau = t % CYCLE;
      const k = (tau - FLIP_AT) / FLIP_DURATION;
      const flip = k >= 0 && k < 1 ? easeInOutCubic(k) * Math.PI * 2 : 0;
      pivot.rotation.y = Math.sin(t * 0.6) * 0.45 + flip + pointer.x * 0.25;
      pivot.rotation.x = Math.sin(t * 0.7) * 0.08 + pointer.y * 0.15;
      pivot.position.y = LOGO_LIFT + Math.sin(t * 0.9) * 25;
      rimA.position.x = Math.cos(t * 0.4) * 2500;
      rimB.position.x = -Math.cos(t * 0.4) * 2500;
      placeStars(tau, t);
    } else {
      pivot.rotation.set(0.12, -0.35, 0);
    }
    renderer.render(scene, camera);
  };

  resize();
  new ResizeObserver(resize).observe(host);
  host.appendChild(renderer.domElement);
  render();
  host.classList.add('is-3d');

  if (!reducedMotion) renderer.setAnimationLoop(render);
}
